import React, { Children, FC } from 'react';

import Link from 'next/link';

import { Text, FormatDate, AuthorFlyout, Flexbox, Avatar } from '@morressier/ts';

import { FaCalendarAlt } from 'react-icons/fa';

import { MAX_PARTICIPANTS_PER_SESSION } from 'constants/networkingSessions';
import { useAuthedUser } from 'hooks/useIsAuthedUser';
import { useAuthContext } from 'store';
import { arrayCommaJoiner } from 'utils/array';
import { toLocaleString } from 'utils/dates';

import { getSpotsAvailability } from './helpers';
import * as Styles from './styles';

export const SessionDateTime: FC<{
  startDate: string;
  endDate: string;
}> = props =>
  props.startDate && props.endDate ? (
    <>
      <FormatDate startDate={props.startDate} endDate={props.endDate} longFormat />
      {' · '}
      {toLocaleString(props.startDate)}
      {' - '}
      {toLocaleString(props.endDate)}
    </>
  ) : null;

export const SessionDateTimeWithIcon: FC<{
  startDate: string;
  endDate: string;
}> = props => (
  <Text
    style={{
      display: 'flex',
      alignItems: 'center',
      marginBottom: '0.5rem',
    }}
    color="secondaryBrand"
    as="div"
  >
    <FaCalendarAlt color="grey" />
    <Styles.HeroInfoLine>
      <Text style={{ color: '#808080' }}>
        <SessionDateTime startDate={props.startDate} endDate={props.endDate} />
      </Text>
    </Styles.HeroInfoLine>
  </Text>
);

const getChairsToRender = (chairs: Chair[], eventId: string, isAuthed: boolean) => {
  const getSeparator = () => {
    if (chairs.length === 1) {
      return '';
    }
    if (chairs.length === 2) {
      return ' and ';
    }
    return ', ';
  };

  if (typeof window === 'undefined') return [];

  const result = chairs.slice(0, 2).map((chair, index) => {
    if (chair.is_activated) {
      return (
        <React.Fragment key={chair._id}>
          <AuthorFlyout
            isPrivate={!isAuthed && !chair.public_profile}
            author={chair as MorressierUser}
            trigger={
              <Link
                href={{
                  pathname: '/event/[eventId]/people/[userId]',
                  query: { eventId, userId: chair.id },
                }}
                passHref
              >
                <Styles.StyledUnderlineLink as="a">{chair.full_name}</Styles.StyledUnderlineLink>
              </Link>
            }
            placement="right"
          />
          {index + 1 === 2 ? '' : getSeparator()}
        </React.Fragment>
      );
    }

    return (
      <>
        <span key={chair._id}>{chair.full_name}</span>
        {index + 1 === 2 ? '' : getSeparator()}
      </>
    );
  });

  const remaining = chairs.length - 2;
  return remaining > 0
    ? [...result, `, and ${remaining} ${remaining === 1 ? 'other' : 'others'}`]
    : result;
};

export const SessionChairsWithIcon: FC<{
  eventId: string;
  chairs?: Chair[];
  chairName?: string[];
  iconColor?: string;
}> = props => {
  const isAuthed = useAuthedUser();
  const { chairs, chairName, eventId } = props;
  const chairNamesToRender = arrayCommaJoiner(chairName?.filter(name => name.length > 0) || []);
  const chairRoles = getChairsToRender(chairs || [], eventId, isAuthed);
  const hasChairs = chairNamesToRender?.length || chairRoles.length;

  return hasChairs ? (
    <>
      <Styles.UserAltFa style={{ marginRight: '10px' }} />{' '}
      <Text as="span">
        {'Chaired by '}
        {chairNamesToRender || chairRoles}
      </Text>
    </>
  ) : null;
};

interface SessionParticipantAvatarsProps {
  hide?: boolean;
  showPlusSign: boolean;
  alignItems?: string;
  participantAvatars: {
    id: string;
    full_name: string;
    picture_url: string;
  }[];
}

export const SessionParticipantAvatars: FC<SessionParticipantAvatarsProps> = ({
  hide,
  showPlusSign,
  children,
  alignItems,
  participantAvatars,
}) => {
  if (hide) return null;

  return (
    <Flexbox alignItems={alignItems}>
      {participantAvatars.map((participant, index) => (
        <Styles.AvatarParticipant
          key={participant.id}
          index={index}
          onlyOne={participantAvatars.length === 1}
        >
          <Avatar
            fullName={participant.full_name}
            size="small"
            customSize="30px"
            customFontSize="15px"
            src={participant.picture_url}
          />
        </Styles.AvatarParticipant>
      ))}
      {showPlusSign && (
        <Styles.AvatarParticipant index={participantAvatars.length} transparent>
          <Avatar fullName="+" size="small" customSize="30px" customFontSize="25px" />
        </Styles.AvatarParticipant>
      )}
      {children}
    </Flexbox>
  );
};

const MAX_PARTICIPANTS_TO_SHOW_AVATAR = 7;
const MAX_PARTICIPANTS_TO_SHOW_NAME = 3;

export const SessionParticipantsWithImage: FC<{
  isPreSession: boolean;
  isHappeningNow: boolean;
  isPostSession: boolean;
  participants: Pick<MorressierUser, 'id' | 'full_name' | 'picture_url'>[];
  reservedSpots: string[];
}> = props => {
  const { state: authState } = useAuthContext();
  const authUser = authState?.profile;
  const { isPreSession, isHappeningNow, isPostSession, participants, reservedSpots } = props;
  const { usedSpots, availableSpots, isFull, hasReservedSpot } = getSpotsAvailability(
    reservedSpots,
    participants,
    authUser?.id,
  );
  const participantsToShowAvatar = participants.slice(0, MAX_PARTICIPANTS_TO_SHOW_AVATAR);
  const remainingParticipantsAvatar = participants.slice(MAX_PARTICIPANTS_TO_SHOW_AVATAR);
  const participantsToShowName = participants.slice(0, MAX_PARTICIPANTS_TO_SHOW_NAME);
  const remainingParticipantsName = participants.slice(MAX_PARTICIPANTS_TO_SHOW_NAME);
  const participantJoinsCountLabels = { 1: 'is in the Session' };
  const participantJoinsCount =
    (isPostSession && 'attended this Session') ||
    participantJoinsCountLabels[participantsToShowAvatar.length] ||
    'are in the Session';
  const isMoreThanOneParticipants = participantsToShowAvatar.length > 1;
  const isLessOrEqual2SpotsLeft = availableSpots > 0 && availableSpots <= 2;

  const getSeparator = (index: number) => {
    const comma = ',';
    if (participantsToShowName.length === 1) {
      return '';
    }

    if (remainingParticipantsAvatar.length === 0) {
      return (
        {
          [participantsToShowName.length - 1]: '',
          [participantsToShowName.length - 2]: ' and ',
        }[index] ?? comma
      );
    }

    return (
      {
        [participantsToShowName.length - 1]: '',
      }[index] ?? comma
    );
  };

  const getXParticipantsMessage = () =>
    participantsToShowName.length > 0 ? (
      <Text as="div">
        {participantsToShowName.map((participant, index) => (
          <>
            <Styles.NameParticipant key={participant.id}>
              {participant.full_name.split(' ')[0]}
              {getSeparator(index)}
            </Styles.NameParticipant>{' '}
          </>
        ))}
        {remainingParticipantsName.length > 0 && (
          <>
            <Text as="span" style={{ display: 'inline-block' }}>
              and
            </Text>{' '}
            <Styles.NameParticipant>{remainingParticipantsName.length} more</Styles.NameParticipant>{' '}
          </>
        )}
        {participantsToShowName.length > 0 && (
          <Text as="span" style={{ display: 'inline-block' }}>
            {participantJoinsCount}
          </Text>
        )}
      </Text>
    ) : null;

  const belowMessages = [
    isPreSession && (
      <Text as="span" color="grey">
        Nobody has joined, yet.
      </Text>
    ),
    isHappeningNow && !participants.length && (
      <Text as="span" color="grey">
        Nobody has joined, be the first one.
      </Text>
    ),
    isPostSession && (
      // TODO: remove this when get participants who attended session is ready to use
      <Text as="span" color="grey">
        this session has ended.
      </Text>
    ),
    isMoreThanOneParticipants && getXParticipantsMessage(),
    usedSpots.isConnected && (
      <Text as="span" color="grey">
        You are already participating on the Networking Session.
      </Text>
    ),
    isFull && !usedSpots.isConnected && (
      <Text as="span" color="grey">
        This Session is full, rooms are limited up to {MAX_PARTICIPANTS_PER_SESSION} people.
      </Text>
    ),
    isLessOrEqual2SpotsLeft && !hasReservedSpot && (
      <Text as="div" color="red" fontWeight="semiBold">
        Only {availableSpots} spot{availableSpots === 1 ? '' : 's'} left
      </Text>
    ),
  ].filter(message => message);

  const shouldAlignColum = isMoreThanOneParticipants || belowMessages.length > 0;

  return (
    <Flexbox
      className="mt1"
      alignItems={isPreSession || !participants?.length ? 'center' : 'flex-start'}
    >
      <Styles.UsersFa style={{ marginRight: '10px', minHeight: '34px' }} />{' '}
      <Flexbox
        flexDirection={shouldAlignColum ? 'column' : 'row'}
        alignItems={shouldAlignColum ? 'flex-start' : 'center'}
      >
        <SessionParticipantAvatars
          hide={!participants}
          alignItems={isMoreThanOneParticipants ? undefined : 'inherit'}
          showPlusSign={remainingParticipantsAvatar.length > 0}
          participantAvatars={participantsToShowAvatar}
        >
          {!isMoreThanOneParticipants && getXParticipantsMessage()}
        </SessionParticipantAvatars>
        {belowMessages}
      </Flexbox>
    </Flexbox>
  );
};
